/**
 * Add User student enroll order helpers (pure — no React/API).
 */

import {
  PROGRAM_TYPES as PMA_PROGRAM_TYPES,
  PROGRAM_NAMES as PMA_PROGRAM_NAMES,
} from '../pma/pmaIdentityPolicy';
import { locationOptionsFromBatches } from '../batch/locationsFromBatches';

/** @deprecated Prefer PMA PROGRAM_NAMES — kept for legacy category codes not on PMA import. */
export const PROGRAM_TYPE_LABELS = Object.freeze({
  ...PMA_PROGRAM_NAMES,
  SLP: 'Systematic learning program',
  JOU: 'Job oriented upskilling',
  WEBINAR: 'Webinar',
});

/** @deprecated Use enrollCentersFromBatches — centers come from batch Location of Center. */
export const ENROLL_CENTERS = Object.freeze([]);

export function enrollCentersFromBatches(batches = [], extra = []) {
  return locationOptionsFromBatches(batches, extra);
}

/** Same codes as PMA student-import program_type. */
export const ENROLL_PROGRAM_TYPES = Object.freeze([...PMA_PROGRAM_TYPES]);

export function programTypeLabel(type) {
  const key = String(type || '')
    .trim()
    .toUpperCase();
  if (!key) return '';
  const name = PROGRAM_TYPE_LABELS[key] || PMA_PROGRAM_NAMES[key];
  return name ? `${key} — ${name}` : key;
}

/**
 * Program type options for Add/Edit — same set as PMA import, plus any extra
 * categoryType values present in the DB that are not on the PMA list.
 */
export function programTypesFromCategories(categories) {
  const seen = new Set();
  const types = [];
  for (const code of ENROLL_PROGRAM_TYPES) {
    seen.add(code);
    types.push({ value: code, label: programTypeLabel(code) });
  }
  for (const category of categories || []) {
    const type = String(category?.categoryType || '')
      .trim()
      .toUpperCase();
    if (!type || seen.has(type)) continue;
    seen.add(type);
    types.push({ value: type, label: programTypeLabel(type) });
  }
  return types;
}

export function categoryTypeOf(item) {
  if (!item || typeof item !== 'object') return '';
  return String(
    item.categoryType || item.category?.categoryType || item.category?.categoryCode || ''
  )
    .trim()
    .toUpperCase();
}

export function filterByProgramType(items, programType) {
  const type = String(programType || '')
    .trim()
    .toUpperCase();
  if (!type) return Array.isArray(items) ? items : [];
  return (items || []).filter((item) => categoryTypeOf(item) === type);
}

/**
 * Mode is locked when the batch, the course, or every course in the program
 * type has a single Online/Offline delivery. "Both" stays choosable.
 */
export function resolveEnrollModeLock({
  batchMode,
  courseDeliveryMode,
  programCourses,
} = {}) {
  const batch = String(batchMode || '').trim();
  if (batch === 'Online' || batch === 'Offline') {
    return { mode: batch, locked: true, reason: 'batch' };
  }
  const course = String(courseDeliveryMode || '').trim();
  if (course === 'Online' || course === 'Offline') {
    return { mode: course, locked: true, reason: 'course' };
  }
  const modes = [
    ...new Set(
      (programCourses || [])
        .map((row) => String(row?.deliveryMode || '').trim())
        .filter((value) => value === 'Online' || value === 'Offline' || value === 'Both')
    ),
  ];
  const concrete = modes.filter((value) => value === 'Online' || value === 'Offline');
  if (concrete.length === 1 && !modes.includes('Both')) {
    return { mode: concrete[0], locked: true, reason: 'program' };
  }
  return { mode: '', locked: false, reason: '' };
}

function ymd(value) {
  if (!value) return '';
  return String(value).split('T')[0];
}

export const EMPTY_ENROLLMENT_FIELDS = Object.freeze({
  courseId: '',
  courseCategoryId: '',
  programType: '',
  modeOfCourse: '',
  locationOfCenter: '',
  batchStartDate: '',
  courseCompletionDate: '',
});

/** Map a batch (list or detail) onto Add User enrollment fields. */
export function enrollmentFieldsFromBatch(batch) {
  if (!batch || typeof batch !== 'object') return {};
  const fields = {};
  if (batch.courseId != null && batch.courseId !== '') {
    fields.courseId = String(batch.courseId);
  }
  const categoryId = batch.course?.categoryId ?? batch.course?.category?.id;
  if (categoryId != null && categoryId !== '') {
    fields.courseCategoryId = String(categoryId);
  }
  const categoryType = batch.course?.category?.categoryType || batch.course?.categoryType;
  if (categoryType) {
    fields.programType = categoryType;
  }
  const start = ymd(batch.startDate);
  if (start) fields.batchStartDate = start;
  const end = ymd(batch.expectedCompletionDate || batch.tentativeEndDate);
  if (end) fields.courseCompletionDate = end;
  if (batch.mode) {
    fields.modeOfCourse = batch.mode;
    fields.locationOfCenter =
      String(batch.mode).trim().toLowerCase() === 'online'
        ? ''
        : String(batch.location || '');
  } else if (batch.location) {
    fields.locationOfCenter = batch.location;
  }
  return fields;
}

/** Live Student ID overlay should read mode/center/program from the selected batch, not a previous one. */
export function identityInputFromBatch(batch) {
  if (!batch || typeof batch !== 'object') {
    return {
      categoryType: '',
      categoryCode: '',
      modeOfCourse: '',
      locationOfCenter: '',
      batchNumber: '',
      batchName: '',
    };
  }
  const online = String(batch.mode || '').trim().toLowerCase() === 'online';
  return {
    categoryType: batch.course?.category?.categoryType || batch.course?.categoryType || '',
    categoryCode: batch.course?.category?.categoryCode || batch.course?.category?.code || '',
    modeOfCourse: batch.mode || '',
    locationOfCenter: online ? '' : String(batch.location || ''),
    batchNumber: batch.batchNumber || '',
    batchName: batch.batchName || batch.name || '',
  };
}

function categoryDisplayName(category) {
  if (!category || typeof category !== 'object') return '';
  const name = String(category.categoryName || category.name || '').trim();
  const code = String(category.categoryCode || category.code || '').trim();
  if (name && code) return `${name} - ${code}`;
  return name || code;
}

/**
 * Program, course, category, mode, center, and dates for the selected batch
 * (list or detail), so the enroll card does not keep a previous batch's values.
 */
export function enrollDisplayFromSelection({
  batch,
  formData = {},
  fallbackProgramType = '',
  fallbackCourseName = '',
  fallbackCategoryName = '',
} = {}) {
  const fromBatch = identityInputFromBatch(batch);
  const course = batch && typeof batch === 'object' ? batch.course : null;
  const category = course && typeof course === 'object' ? course.category : null;
  const mode = formData.modeOfCourse || fromBatch.modeOfCourse || '';
  const center = formData.locationOfCenter || fromBatch.locationOfCenter || '';
  return {
    programType:
      programTypeLabel(formData.programType) ||
      programTypeLabel(fromBatch.categoryType) ||
      fallbackProgramType ||
      '',
    courseName: (course && (course.courseName || course.name)) || fallbackCourseName || '',
    categoryName: categoryDisplayName(category) || fallbackCategoryName || '',
    mode,
    center,
    batchStartDate: formData.batchStartDate || ymd(batch?.startDate) || '',
    courseCompletionDate:
      formData.courseCompletionDate ||
      ymd(batch?.expectedCompletionDate || batch?.tentativeEndDate) ||
      '',
    showCenter: String(mode).trim().toLowerCase() === 'offline' || Boolean(String(center).trim()),
  };
}

/**
 * Program / mode / center for the suggested VE Student ID — filled form
 * fields win over the selected batch.
 */
export function identityInputFromFilledEnrollment({
  formData = {},
  batch,
  selectedCategory,
} = {}) {
  const fromBatch = identityInputFromBatch(batch);
  const mode = formData.modeOfCourse || fromBatch.modeOfCourse || '';
  const online = String(mode).trim().toLowerCase() === 'online';
  const categoryType =
    String(formData.programType || '').trim() ||
    fromBatch.categoryType ||
    String(
      selectedCategory?.categoryType ||
        selectedCategory?.categoryCode ||
        selectedCategory?.code ||
        ''
    ).trim();
  const categoryCode = String(
    selectedCategory?.categoryCode || selectedCategory?.code || fromBatch.categoryCode || ''
  ).trim();
  return {
    categoryType,
    categoryCode,
    modeOfCourse: mode,
    locationOfCenter: online
      ? ''
      : String(formData.locationOfCenter || fromBatch.locationOfCenter || '').trim(),
    batchNumber: fromBatch.batchNumber,
    batchName: fromBatch.batchName,
  };
}
